import { UserCog, ShieldAlert } from "lucide-react";
import type { AdminUser } from "@/components/admin/_mockData";

/**
 * Red warning dialog confirming an impersonation session start. Lists
 * what gets logged and the 30-min TTL. ROADS-476 deliverable A5
 * (security-sensitive — requires security review before merge of the
 * backend route this confirms).
 */
interface ImpersonateWarningModalProps {
  user: AdminUser;
  open: boolean;
  onCancel: () => void;
  onConfirm?: (reason: string) => void;
}

export default function ImpersonateWarningModal({
  user,
  open,
  onCancel,
}: ImpersonateWarningModalProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="impersonate-modal-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-6"
      data-testid="admin-impersonate-warning-modal"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start gap-3.5 border-b border-red-800 bg-red-700 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20">
            <UserCog aria-hidden="true" className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2
              id="impersonate-modal-title"
              className="text-lg font-bold text-white"
            >
              Impersonate user — security-sensitive
            </h2>
            <p className="text-xs font-medium text-white/80">
              Required for debugging customer-reported bugs. Use sparingly.
            </p>
          </div>
        </div>
        <div className="space-y-4 p-5">
          <div className="space-y-2.5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center gap-2">
              <ShieldAlert
                aria-hidden="true"
                className="w-4 h-4 text-amber-700"
              />
              <h3 className="text-sm font-bold text-amber-800">
                What gets logged
              </h3>
            </div>
            <ul className="space-y-2 text-xs leading-relaxed text-slate-700">
              {[
                "impersonate.start with target user_id + operator + reason",
                "Every read/write action during the session is attributed to YOU, not the target",
                "impersonate.end with duration + cause (manual exit / auto-expire)",
                "Short-TTL session: auto-revokes after 30 min, stored under impersonation:* Redis prefix",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-amber-600"
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: user.avatarHex }}
            >
              {user.initials}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">
                {user.name}
              </div>
              <div className="truncate text-[11px] text-slate-500">
                Target session will mirror real entitlements + UI state
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="impersonate-reason"
              className="block text-[11px] font-semibold text-slate-700"
            >
              Reason (required, audit log)
            </label>
            <textarea
              id="impersonate-reason"
              className="mt-1.5 w-full rounded-lg border border-slate-200 p-3 text-xs text-slate-900"
              defaultValue="User reports rule-diff blank on CA → TX swap. Reproducing."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700"
          >
            <UserCog aria-hidden="true" className="w-3.5 h-3.5" />
            Start 30-min session
          </button>
        </div>
      </div>
    </div>
  );
}
